<template>
  <div class="courses-filter-desktop">
    <ul class="courses-filter-desktop__list">
      <li v-if="computedFilters?.companies.length" class="courses-filter-desktop__item">
        <UiGroupedCheckboxesFilter
          title="Партнёры"
          :list="computedFilters.companies"
          :active="((filtersQuery.companies || []) as string[])"
          has-scroll
          @submit="(event) => {
            handleSubmit('companies', event)
            sendCatalogClickFilterPartner(sendClickAnalyticsContent(event))
          }"
          @visible:menu="sendCatalogShowFilterPartner(sendShowAnalyticsContent)"
        />
      </li>

      <li v-if="computedFilters?.levels.length" class="courses-filter-desktop__item">
        <UiCheckboxesFilter
          title="Уровень"
          :list="computedFilters.levels"
          :active="((filtersQuery.levels || []) as string[])"
          @submit="(event) => {
            handleSubmit('levels', event)
            sendCatalogClickFilterLevel(sendClickAnalyticsContent(event))
          }"
          @visible:menu="sendCatalogShowFilterLevel(sendShowAnalyticsContent)"
        />
      </li>

      <li v-if="computedFilters?.duration" class="courses-filter-desktop__item">
        <UiMinMaxFilter
          :title="durationTitle"
          :data="computedFilters.duration"
          :min="(filtersQuery.duration_min as string)"
          :max="(filtersQuery.duration_max as string)"
          @submit="(event) => {
            handleSubmitMinMax(event)
            sendCatalogClickFilterDuration(sendMinMaxClickAnalyticsContent(event))
          }"
          @visible:menu="sendCatalogShowFilterDuration(sendShowAnalyticsContent)"
        />
      </li>

      <li v-if="computedFilters?.languages.length" class="courses-filter-desktop__item">
        <UiCheckboxesFilter
          title="Язык"
          :list="computedFilters.languages"
          :active="((filtersQuery.languages || []) as string[])"
          @submit="(event) => {
            handleSubmit('languages', event)
            sendCatalogClickFilterLanguage(sendClickAnalyticsContent(event))
          }"
          @visible:menu="sendCatalogShowFilterLanguage(sendShowAnalyticsContent)"
        />
      </li>
    </ul>

    <UiSortFilter
      v-if="computedFilters?.sorting"
      show-full-button
      :sorting="computedFilters.sorting"
      :current-value="((filtersQuery.sorting || computedFilters.sorting[0]?.value) as string)"
      class="courses-filter-desktop__sorting"
      @change="(event) => {
        handleSubmit('sorting', event)
        sendCatalogClickCoursesSorting(sendClickAnalyticsContent(event))
      }"
      @visible:menu="sendCatalogShowCoursesSorting()"
    />
  </div>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import UiGroupedCheckboxesFilter from '@academica-box/components/Filter/desktop/UiGroupedCheckboxesFilter/UiGroupedCheckboxesFilter.vue'
  import UiCheckboxesFilter from '@academica-box/components/Filter/desktop/UiCheckboxesFilter/UiCheckboxesFilter.vue'
  import UiMinMaxFilter from '@academica-box/components/Filter/desktop/UiMinMaxFilter/UiMinMaxFilter.vue'
  import UiSortFilter from '@academica-box/components/Filter/desktop/UiSortFilter/UiSortFilter.vue'
  import { pluralize } from '@academica-box/utils/scripts/pluralize'
  import { useRouteQueryParams, setRouteQueryParams } from '@academica-box/composables/useRouteQueryParam'
  import { FilterKeys } from '@academica-box/types'
  import { useDirectionsStore } from '@skillbox/academica-box/src/composables/store/directions'
  import { useFiltersStore } from '@/store/filters'
  import {
    sendCatalogShowFilterPartner,
    sendCatalogClickFilterPartner,
    sendCatalogShowFilterLevel,
    sendCatalogClickFilterLevel,
    sendCatalogShowFilterDuration,
    sendCatalogClickFilterDuration,
    sendCatalogShowFilterLanguage,
    sendCatalogClickFilterLanguage,
    sendCatalogShowCoursesSorting,
    sendCatalogClickCoursesSorting,
  } from '@/pages/catalog/analytics'

  const filtersStore = useFiltersStore()
  const { computedFilters } = storeToRefs(filtersStore)

  const directionsStore = useDirectionsStore()
  const { activeDirection, activeSection } = storeToRefs(directionsStore)

  const sendClickAnalyticsContent = (event: string | string[]) => {
    return {
      direction_id: activeDirection.value?.slug ?? '',
      topics_id: activeSection.value?.slug ?? '',
      filter_value: typeof event !== 'string' ? event?.join(', ') : event,
    }
  }

  const sendMinMaxClickAnalyticsContent = (event: {min: string, max: string, checkbox: boolean}, checkbox?: boolean) => {
    return {
      direction_id: activeDirection.value?.slug ?? '',
      topics_id: activeSection.value?.slug ?? '',
      filter_value: `min: ${event.min || 'none'}, max: ${event.max || 'none'} ${checkbox ? `checkbox: ${event.checkbox}` : ''}`,
    }
  }

  const sendShowAnalyticsContent = computed(() => {
    return {
      direction_id: activeDirection.value?.slug ?? '',
      topics_id: activeSection.value?.slug ?? '',
    }
  })

  const filtersQuery = useRouteQueryParams({
    [FilterKeys.companies]: 'array',
    [FilterKeys.levels]: 'array',
    [FilterKeys.duration_min]: 'string',
    [FilterKeys.duration_max]: 'string',
    [FilterKeys.languages]: 'array',
    [FilterKeys.sorting]: 'string',
    [FilterKeys.page]: 'string',
  })

  const durationTitle = computed(() => {
    const min = filtersQuery.value[FilterKeys.duration_min] as string
    const max = filtersQuery.value[FilterKeys.duration_max] as string

    if (min && !max) {
      return `От ${min} ${pluralize(Number(min), ['часа', 'часов', 'часов'])}`
    } else if (min && max) {
      return `От ${min} до ${max} ${pluralize(Number(max), ['часа', 'часов', 'часов'])}`
    } else if (!min && max) {
      return `До ${max} ${pluralize(Number(max), ['часа', 'часов', 'часов'])}`
    } else {
      return 'Длительность'
    }
  })

  const handleSubmit = (key: string, value: string[] | string) => {
    setRouteQueryParams(filtersQuery, {
      [key]: value,
      [FilterKeys.page]: '',
    })
  }

  const handleSubmitMinMax = (value: { min: string, max: string }) => {
    setRouteQueryParams(filtersQuery, {
      [FilterKeys.duration_min]: value.min,
      [FilterKeys.duration_max]: value.max,
      [FilterKeys.page]: '',
    })
  }
</script>

<style lang="scss">
  @use "@academica-box/utils/styles/mixins/UiMedia" as *;
  @use "@academica-box/utils/styles/mixins/UiReset" as *;

  .courses-filter-desktop {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .courses-filter-desktop__list {
    @include ulres;

    display: flex;
    flex-wrap: wrap;
    margin-bottom: -8px;
  }

  .courses-filter-desktop__item {
    flex-shrink: 0;
    margin-bottom: 8px;

    &:not(:last-child) {
      margin-right: 6px;
    }
  }

  .courses-filter-desktop__sorting {
    flex-shrink: 0;

    @include media(lg) {
      margin-left: 14px;
    }

    @include media(xl) {
      margin-left: 48px;
    }

    @include media(xxl) {
      margin-left: 194px;
    }
  }

  .courses-filter-desktop__toggle {
    &::before {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
    }
  }
</style>
